/**
 * NetworkHarvestable
 *
 * Generic client entity for V1 harvestable fields: ice asteroids, gas pockets, wreckage.
 * Mirrors NetworkAsteroid behavior (health bar, mining sparks, explosion debris) but
 * parameterized by a GraphicsPreset for visual identity and a `kind` discriminator
 * for entity-type tagging.
 *
 * Three concrete classes (NetworkIceAsteroid, NetworkGasPocket, NetworkWreckage) are
 * exported as thin subclasses below.
 */

import { Container, Graphics, Sprite, Text as PixiText, Texture } from 'pixi.js';
import type { AsteroidSize, SelectionInfo, EntityType } from '@astra/shared';
import {
  AsteroidGraphics,
  createTextureExplosion,
  ICE_PRESET,
  GAS_PRESET,
  WRECKAGE_PRESET,
  type GraphicsPreset,
} from './AsteroidGraphics';
import { EventEmitter } from '../core/EventEmitter';
import { DamageableEntity } from './base/DamageableEntity';
import { assetLoader } from '../core/AssetLoader';

// Shape mass values (same as asteroids — they're physically rocks/debris/dense clouds)
const HARVESTABLE_MASS: Record<AsteroidSize, number> = {
  small: 400,
  medium: 900,
  large: 2000,
  huge: 4500,
};

function degToRad(deg: number): number {
  return deg * (Math.PI / 180);
}

interface MiningSpark {
  sprite: Sprite;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

// Texture cache keyed by kind (one tiny star texture each — color comes from tint).
const sparkTextureCache: Map<string, Texture> = new Map();
function getSparkTexture(): Texture {
  const key = 'harvestable-mining-spark';
  const cached = sparkTextureCache.get(key);
  if (cached) return cached;
  const gfx = new Graphics();
  gfx.star(4, 4, 4, 4, 2);
  gfx.fill({ color: 0xffffff, alpha: 1 });
  const tex = assetLoader.generateTexture(gfx, key);
  gfx.destroy();
  sparkTextureCache.set(key, tex);
  return tex;
}

export type HarvestableClientKind = 'ice_asteroid' | 'gas_pocket' | 'wreckage';

export interface HarvestableData {
  id: string;
  x: number;
  y: number;
  size: string;
  resourceType: string;
  resourceTier: number;
  resourceAmount: number;
  health: number;
  maxHealth: number;
  // gas pockets only
  nebulaId?: string;
  // ice/wreckage only
  zoneId?: string;
}

const KIND_LABEL: Record<HarvestableClientKind, string> = {
  ice_asteroid: 'Ice',
  gas_pocket: 'Gas Pocket',
  wreckage: 'Wreckage',
};

export class NetworkHarvestable extends DamageableEntity {
  readonly id: string;
  readonly entityType: EntityType;
  readonly kind: HarvestableClientKind;

  readonly selectionRadius: number;
  readonly isHostile: boolean = false;
  readonly collisionRadius: number;
  readonly mass: number;

  public size: AsteroidSize;
  public resourceType: string;
  public resourceTier: number;
  public resourceAmount: number;

  public angularVelocity: number = 0;
  private _angle: number = 0;

  protected graphics: AsteroidGraphics;
  protected preset: GraphicsPreset;

  // Mining progress bar (drawn in this container while a miner is active; HP not shown)
  private harvHealthBarContainer: Container | null = null;
  private harvHealthBarGraphics: Graphics | null = null;
  private harvHealthBarVisible: boolean = false;
  private harvProgressLabel: PixiText | null = null;

  // Mining sparks
  private miningSparks: MiningSpark[] = [];
  private miningSparkTimer: number = 0;
  private minerPositions: Map<string, { x: number; y: number }> = new Map();
  private sparkParentContainer: Container | null = null;

  public events: EventEmitter = new EventEmitter();

  public get angle(): number { return this._angle; }
  public set angle(value: number) {
    this._angle = value;
    this.rotation = degToRad(value);
  }

  constructor(kind: HarvestableClientKind, data: HarvestableData, preset: GraphicsPreset) {
    super();
    this.position.set(data.x, data.y);

    this.id = data.id;
    this.kind = kind;
    this.entityType = kind;
    this.size = data.size as AsteroidSize;
    this.resourceType = data.resourceType;
    this.resourceTier = data.resourceTier;
    this.resourceAmount = data.resourceAmount;
    this.mass = HARVESTABLE_MASS[this.size];
    this.preset = preset;

    this.graphics = new AsteroidGraphics(0, 0, this.size, this.resourceType, preset);
    this.addChild(this.graphics);

    this.collisionRadius = this.graphics.getRadius();
    this.selectionRadius = this.collisionRadius * 1.2;

    this.hull = data.health;
    this.maxHull = data.maxHealth;
    this.shield = 0;
    this.maxShield = 0;

    this._angle = Math.random() * 360;
    this.rotation = degToRad(this._angle);
    this.angularVelocity = (Math.random() - 0.5) * 40;

    const driftSpeed = Math.random() * 10;
    const driftAngle = Math.random() * Math.PI * 2;
    this.velocityX = Math.cos(driftAngle) * driftSpeed;
    this.velocityY = Math.sin(driftAngle) * driftSpeed;

    this.createHealthBar();
  }

  getSelectionInfo(): SelectionInfo {
    const sizeLabel = this.size.charAt(0).toUpperCase() + this.size.slice(1);
    const kindLabel = KIND_LABEL[this.kind];
    return {
      type: this.kind,
      id: this.id,
      name: `${sizeLabel} ${this.resourceType} ${kindLabel}`,
      x: this.x,
      y: this.y,
      data: {
        size: this.size,
        resourceType: this.resourceType,
        resourceAmount: this.resourceAmount,
        health: this.hull,
        maxHealth: this.maxHull,
      },
    };
  }

  // Override the base class createHealthBar. We don't show HP; instead this container
  // hosts the mining-progress bar (0..1 toward the next whole resource unit), populated
  // by updateMiningProgress() while a miner is active.
  protected override createHealthBar(): void {
    const radius = this.graphics.getRadius();
    this.harvHealthBarContainer = new Container();
    this.harvHealthBarContainer.position.set(this.x, this.y - radius - 15);
    this.harvHealthBarContainer.zIndex = 100;
    this.harvHealthBarContainer.visible = false;
    this.harvHealthBarGraphics = new Graphics();
    this.harvHealthBarContainer.addChild(this.harvHealthBarGraphics);
  }

  public getHealthBarContainer(): Container | null { return this.harvHealthBarContainer; }

  // HP-style bar is intentionally never shown; mining progress drives this slot instead.
  protected override showHealthBar(): void { /* no-op */ }

  /** Draw the mining progress bar (0..1 toward next whole unit) + "→ +1 RESOURCE" label. */
  public updateMiningProgress(progress: number, resourceType: string): void {
    if (!this.harvHealthBarContainer || !this.harvHealthBarGraphics) return;
    const clamped = Math.max(0, Math.min(1, progress));
    const barWidth = Math.max(40, this.graphics.getRadius() * 1.5);
    const barHeight = 5;
    const x = -barWidth / 2;
    const y = 0;
    this.harvHealthBarGraphics.clear();
    // Backdrop
    this.harvHealthBarGraphics.rect(x - 1, y - 1, barWidth + 2, barHeight + 2);
    this.harvHealthBarGraphics.fill({ color: 0x101820, alpha: 0.85 });
    // Fill (resource-tinted, fallback cyan)
    const fillColor = this.preset.colorMap[resourceType] || 0x44ffaa;
    this.harvHealthBarGraphics.rect(x, y, barWidth * clamped, barHeight);
    this.harvHealthBarGraphics.fill({ color: fillColor, alpha: 1 });
    // Border
    this.harvHealthBarGraphics.rect(x - 1, y - 1, barWidth + 2, barHeight + 2);
    this.harvHealthBarGraphics.stroke({ width: 1, color: 0x88aabb, alpha: 0.8 });

    // Label "→ +1 RESOURCE"
    const labelText = `→ +1 ${resourceType.split('_')[0].toUpperCase()}`;
    if (!this.harvProgressLabel) {
      this.harvProgressLabel = new PixiText({
        text: labelText,
        style: { fontFamily: 'monospace', fontSize: 11, fill: 0xcceeff, stroke: { color: 0x000000, width: 2 } },
      });
      this.harvProgressLabel.anchor.set(0.5, 1);
      this.harvProgressLabel.position.set(0, -2);
      this.harvHealthBarContainer.addChild(this.harvProgressLabel);
    } else if (this.harvProgressLabel.text !== labelText) {
      this.harvProgressLabel.text = labelText;
    }

    this.harvHealthBarVisible = true;
    this.harvHealthBarContainer.visible = true;
  }

  public hideMiningProgress(): void {
    if (this.harvHealthBarContainer) {
      this.harvHealthBarVisible = false;
      this.harvHealthBarContainer.visible = false;
    }
  }

  public addMiner(minerId: string, x: number, y: number): void {
    this.minerPositions.set(minerId, { x, y });
  }

  public updateMinerPosition(minerId: string, x: number, y: number): void {
    if (this.minerPositions.has(minerId)) this.minerPositions.set(minerId, { x, y });
  }

  public removeMiner(minerId: string): void {
    this.minerPositions.delete(minerId);
    if (this.minerPositions.size === 0) this.hideMiningProgress();
  }

  public get isBeingMined(): boolean { return this.minerPositions.size > 0; }

  public setSparkParent(parent: Container): void { this.sparkParentContainer = parent; }

  private spawnMiningSparks(): void {
    if (!this.sparkParentContainer || this.minerPositions.size === 0) return;
    const tex = getSparkTexture();
    const resourceColor = this.preset.colorMap[this.resourceType] || this.preset.defaultColor;
    const colors = [resourceColor, resourceColor, 0xff6600, 0xffaa00, 0xffffff];
    const radius = this.graphics.getRadius();
    for (const [, minerPos] of this.minerPositions) {
      const angleToMiner = Math.atan2(minerPos.y - this.y, minerPos.x - this.x);
      const spawnX = this.x + Math.cos(angleToMiner) * radius;
      const spawnY = this.y + Math.sin(angleToMiner) * radius;
      const count = 1 + Math.floor(Math.random() * 2);
      for (let i = 0; i < count; i++) {
        const sprite = new Sprite(tex);
        sprite.anchor.set(0.5);
        sprite.position.set(spawnX, spawnY);
        sprite.tint = colors[Math.floor(Math.random() * colors.length)];
        sprite.scale.set(0.3 + Math.random() * 0.4);
        sprite.zIndex = 16;
        this.sparkParentContainer.addChild(sprite);
        const spreadAngle = angleToMiner + (Math.random() - 0.5) * Math.PI * 0.6;
        const speed = 50 + Math.random() * 70;
        this.miningSparks.push({
          sprite,
          vx: Math.cos(spreadAngle) * speed,
          vy: Math.sin(spreadAngle) * speed,
          life: 250 + Math.random() * 250,
          maxLife: 250 + Math.random() * 250,
        });
      }
    }
  }

  private updateMiningSparks(delta: number): void {
    const dt = delta / 1000;
    for (let i = this.miningSparks.length - 1; i >= 0; i--) {
      const spark = this.miningSparks[i];
      spark.sprite.x += spark.vx * dt;
      spark.sprite.y += spark.vy * dt;
      spark.vx *= 0.97;
      spark.vy *= 0.97;
      spark.life -= delta;
      const lifeRatio = Math.max(0, spark.life / spark.maxLife);
      spark.sprite.alpha = lifeRatio;
      spark.sprite.scale.set(0.2 + 0.3 * lifeRatio);
      if (spark.life <= 0) {
        spark.sprite.destroy();
        this.miningSparks.splice(i, 1);
      }
    }
  }

  update(delta: number): void {
    if (this.isDestroyed) return;
    const dt = delta / 1000;
    this._angle += this.angularVelocity * dt;
    this.rotation = degToRad(this._angle);
    this.x += this.velocityX * dt;
    this.y += this.velocityY * dt;
    this.velocityX *= 0.995;
    this.velocityY *= 0.995;
    if (this.harvHealthBarContainer) {
      const radius = this.graphics.getRadius();
      this.harvHealthBarContainer.position.set(this.x, this.y - radius - 15);
    }
    if (this.minerPositions.size > 0 && this.sparkParentContainer) {
      this.miningSparkTimer += delta;
      if (this.miningSparkTimer >= 60) {
        this.spawnMiningSparks();
        this.miningSparkTimer = 0;
      }
    }
    this.updateMiningSparks(delta);
    this.updateStunState();
  }

  updateFromServer(data: Partial<HarvestableData>): void {
    if (data.health !== undefined) {
      this.hull = data.health;
      if (this.hull < this.maxHull) {
        this.graphics.alpha = 1.5;
        setTimeout(() => { if (this.graphics) this.graphics.alpha = 1; }, 50);
      }
    }
    if (data.resourceAmount !== undefined) this.resourceAmount = data.resourceAmount;
  }

  // Harvestables are not damaged by ramming (same as asteroids)
  public handleCollision(): boolean { return false; }

  protected override onDestroy(): void { this.explode(); }

  public explode(): void {
    if (this.parent) {
      const color = this.preset.colorMap[this.resourceType] || this.preset.defaultColor;
      // Snapshot the visible graphics (this container includes glow + body/SVG)
      createTextureExplosion(
        this.parent,
        this,
        this.x,
        this.y,
        this.rotation,
        color,
        this.graphics.getRadius(),
      );
    }
    if (this.harvHealthBarContainer) this.harvHealthBarContainer.visible = false;
  }

  getResourceColor(): number {
    return this.preset.colorMap[this.resourceType] || this.preset.defaultColor;
  }

  getRadius(): number { return this.graphics.getRadius(); }

  override destroy(): void {
    if (this.harvHealthBarContainer) {
      this.harvHealthBarContainer.destroy({ children: true });
      this.harvHealthBarContainer = null;
    }
    this.harvHealthBarGraphics = null;
    this.harvProgressLabel = null;
    for (const spark of this.miningSparks) spark.sprite.destroy();
    this.miningSparks = [];
    super.destroy();
  }
}

// ==================== CONCRETE SUBCLASSES ====================
// Thin wrappers that pick the right preset + kind tag.

export class NetworkIceAsteroid extends NetworkHarvestable {
  constructor(data: HarvestableData) { super('ice_asteroid', data, ICE_PRESET); }
}

export class NetworkGasPocket extends NetworkHarvestable {
  private svgSprite: Sprite | null = null;
  private glowGraphics: Graphics | null = null;

  constructor(data: HarvestableData) {
    super('gas_pocket', data, GAS_PRESET);
    this.replaceWithSvgVisual();
  }

  private replaceWithSvgVisual(): void {
    const texture = assetLoader.getTexture('gas_pocket_svg');
    if (!texture) return; // Fallback to procedural if SVG not loaded

    // Hide the procedural asteroid graphics (keep for getRadius() calls)
    this.graphics.visible = false;

    const resourceColor = this.preset.colorMap[this.resourceType] || this.preset.defaultColor;
    const glowColor = this.preset.glowMap[this.resourceType] || resourceColor;
    const radius = this.graphics.getRadius();

    // Scale: SVG is 300x300, we want it to cover diameter = radius * 2
    const targetDiameter = radius * 2;
    const svgSize = 300;
    const scale = targetDiameter / svgSize;

    // Glow layers behind the SVG shape
    this.glowGraphics = new Graphics();
    this.glowGraphics.circle(0, 0, radius * 1.15);
    this.glowGraphics.fill({ color: glowColor, alpha: 0.06 });
    this.glowGraphics.circle(0, 0, radius * 0.95);
    this.glowGraphics.fill({ color: glowColor, alpha: 0.08 });
    this.glowGraphics.circle(0, 0, radius * 0.75);
    this.glowGraphics.fill({ color: glowColor, alpha: 0.10 });
    this.addChildAt(this.glowGraphics, 0);

    // SVG sprite — tinted with the resource color
    this.svgSprite = new Sprite(texture);
    this.svgSprite.anchor.set(0.5);
    this.svgSprite.scale.set(scale);
    this.svgSprite.tint = resourceColor;
    this.svgSprite.alpha = 0.85;
    this.addChild(this.svgSprite);
  }

  override destroy(): void {
    if (this.glowGraphics) {
      this.glowGraphics.destroy();
      this.glowGraphics = null;
    }
    if (this.svgSprite) {
      this.svgSprite.destroy();
      this.svgSprite = null;
    }
    super.destroy();
  }
}

export class NetworkWreckage extends NetworkHarvestable {
  constructor(data: HarvestableData) { super('wreckage', data, WRECKAGE_PRESET); }
}
